import React, { useState, useEffect } from 'react'
import { Plus, Trash2, Fuel, IndianRupee, Calendar, Clock, Truck } from 'lucide-react'
import { useData } from '../context/DataContext'

function FuelForm({ fuelEntry, onSubmit, onCancel, isSubmitting }) {
  const { state } = useData()
  const { vehicles, loading } = state

  const [formData, setFormData] = useState({
    bunkName: '',
    fuelDate: new Date().toISOString().split('T')[0],
    billNumber: '',
    paymentMethod: 'cash',
    amountGivenToBunk: '',
    notes: '',
    description: '',
    fuelItems: [{
      vehicleId: '',
      liters: '',
      amount: '',
      vehicleHours: '',
      fuelDate: new Date().toISOString().split('T')[0],
      billNumber: '',
      paymentMethod: 'cash'
    }]
  })

  const [errors, setErrors] = useState({})

  // Populate form when editing
  useEffect(() => {
    if (fuelEntry) {
      setFormData({
        bunkName: fuelEntry.bunkName || '',
        fuelDate: fuelEntry.fuelDate || new Date().toISOString().split('T')[0],
        billNumber: fuelEntry.billNumber || '',
        paymentMethod: fuelEntry.paymentMethod || 'cash',
        amountGivenToBunk: fuelEntry.amountGivenToBunk !== null && fuelEntry.amountGivenToBunk !== undefined ? fuelEntry.amountGivenToBunk : '',
        notes: fuelEntry.notes || '',
        description: fuelEntry.description || '',
        fuelItems: fuelEntry.fuelItems && fuelEntry.fuelItems.length > 0 ? fuelEntry.fuelItems.map(item => ({
          vehicleId: item.vehicleId || item.vehicle_id || '',
          liters: item.liters !== null && item.liters !== undefined ? item.liters : '',
          amount: item.amount !== null && item.amount !== undefined ? item.amount : '',
          vehicleHours: item.vehicleHours !== null && item.vehicleHours !== undefined ? item.vehicleHours : '',
          fuelDate: item.fuelDate || new Date().toISOString().split('T')[0],
          billNumber: item.billNumber || '',
          paymentMethod: item.paymentMethod || 'cash'
        })) : [{
          vehicleId: '',
          liters: '',
          amount: '',
          vehicleHours: '',
          fuelDate: new Date().toISOString().split('T')[0],
          billNumber: '',
          paymentMethod: 'cash'
        }]
      })
    }
  }, [fuelEntry])

  // Handle vehicle deletion - clear selection if vehicle no longer exists
  useEffect(() => {
    if (vehicles && vehicles.length > 0) {
      const vehicleIds = vehicles.map(v => v.id)
      setFormData(prev => ({
        ...prev,
        fuelItems: prev.fuelItems.map(item => ({
          ...item,
          vehicleId: item.vehicleId && vehicleIds.includes(item.vehicleId) ? item.vehicleId : ''
        }))
      }))
    }
  }, [vehicles])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleFuelItemChange = (index, field, value) => {
    const newFuelItems = [...formData.fuelItems]
    newFuelItems[index] = { ...newFuelItems[index], [field]: value }
    setFormData(prev => ({ ...prev, fuelItems: newFuelItems }))
    
    // Clear error when user starts typing
    const errorKey = `fuelItem${index}${field.charAt(0).toUpperCase() + field.slice(1)}`
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }))
    }
  }

  const addFuelItem = () => {
    setFormData(prev => ({
      ...prev,
      fuelItems: [...prev.fuelItems, {
        vehicleId: '',
        liters: '',
        amount: '',
        vehicleHours: '',
        fuelDate: new Date().toISOString().split('T')[0],
        billNumber: '',
        paymentMethod: 'cash'
      }]
    }))
  }

  const removeFuelItem = (index) => {
    if (formData.fuelItems.length > 1) {
      const newFuelItems = formData.fuelItems.filter((_, i) => i !== index)
      setFormData(prev => ({ ...prev, fuelItems: newFuelItems }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate common fields
    if (!formData.bunkName.trim()) {
      newErrors.bunkName = 'Bunk name is required'
    }
    if (!formData.fuelDate) {
      newErrors.fuelDate = 'Fuel date is required'
    }
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required'
    }
    if (!formData.amountGivenToBunk || parseFloat(formData.amountGivenToBunk) < 0) {
      newErrors.amountGivenToBunk = 'Valid amount given to bunk is required'
    }

    // Validate fuel items
    if (formData.fuelItems && formData.fuelItems.length > 0) {
      formData.fuelItems.forEach((item, index) => {
        if (!item.vehicleId) {
          newErrors[`fuelItem${index}VehicleId`] = `Vehicle selection is required for fuel item ${index + 1}`
        }
        if (!item.liters || parseFloat(item.liters) <= 0) {
          newErrors[`fuelItem${index}Liters`] = `Valid liters is required for fuel item ${index + 1}`
        }
        if (!item.amount || parseFloat(item.amount) <= 0) {
          newErrors[`fuelItem${index}Amount`] = `Valid amount is required for fuel item ${index + 1}`
        }
        if (!item.vehicleHours || parseFloat(item.vehicleHours) < 0) {
          newErrors[`fuelItem${index}VehicleHours`] = `Valid vehicle hours is required for fuel item ${index + 1}`
        }
        if (!item.fuelDate) {
          newErrors[`fuelItem${index}FuelDate`] = `Fuel date is required for fuel item ${index + 1}`
        }
        if (!item.billNumber.trim()) {
          newErrors[`fuelItem${index}BillNumber`] = `Bill number is required for fuel item ${index + 1}`
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const fuelData = {
      bunkName: formData.bunkName.trim(),
      fuelDate: formData.fuelDate,
      billNumber: formData.billNumber.trim(),
      paymentMethod: formData.paymentMethod,
      amountGivenToBunk: parseFloat(formData.amountGivenToBunk || 0),
      notes: formData.notes.trim(),
      description: formData.description.trim(),
      fuelItems: formData.fuelItems.map(item => ({
        vehicleId: item.vehicleId,
        liters: parseFloat(item.liters || 0),
        amount: parseFloat(item.amount || 0),
        vehicleHours: parseFloat(item.vehicleHours || 0),
        fuelDate: item.fuelDate,
        billNumber: item.billNumber.trim(),
        paymentMethod: item.paymentMethod
      }))
    }

    await onSubmit(fuelData)
  }

  const totalLiters = formData.fuelItems.reduce((sum, item) => sum + parseFloat(item.liters || 0), 0)
  const totalAmount = formData.fuelItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
  const totalVehicleHours = formData.fuelItems.reduce((sum, item) => sum + parseFloat(item.vehicleHours || 0), 0)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Common Information */}
       <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
         <h3 className="font-medium text-blue-900 mb-4">Common Information</h3>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
           {/* Bunk Name */}
           <div>
             <label htmlFor="bunkName" className="block text-sm font-medium text-blue-700 mb-1">
               Bunk Name *
             </label>
             <input
               type="text"
               id="bunkName"
               name="bunkName"
               value={formData.bunkName}
               onChange={handleChange}
               className={`input ${errors.bunkName ? 'border-red-500 focus:ring-red-500' : ''}`}
               placeholder="Enter bunk name"
             />
             {errors.bunkName && <p className="text-red-500 text-xs mt-1">{errors.bunkName}</p>}
           </div>
         </div>
       </div>

      {/* Fuel Items */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">Fuel Items</h3>
                     <button
             type="button"
             onClick={addFuelItem}
             className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-red text-white text-sm rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
           >
             <Plus className="h-4 w-4 mr-1" />
             Add Item
           </button>
        </div>

        <div className="space-y-4">
          {formData.fuelItems.map((item, index) => (
            <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Fuel Item {index + 1}</h4>
                {formData.fuelItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFuelItem(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 {/* Vehicle Selection */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Vehicle *
                   </label>
                   <select
                     value={item.vehicleId}
                     onChange={(e) => handleFuelItemChange(index, 'vehicleId', e.target.value)}
                     className={`input ${errors[`fuelItem${index}VehicleId`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                     disabled={loading}
                   >
                     <option value="">Choose a vehicle...</option>
                     {loading ? (
                       <option value="" disabled>Loading vehicles...</option>
                     ) : vehicles && vehicles.length > 0 ? (
                       vehicles.map(vehicle => (
                         <option key={vehicle.id} value={vehicle.id}>
                           {vehicle.registrationNumber} - {vehicle.type}
                         </option>
                       ))
                     ) : (
                       <option value="" disabled>No vehicles available</option>
                     )}
                   </select>
                   {errors[`fuelItem${index}VehicleId`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}VehicleId`]}</p>}
                 </div>

                 {/* Liters */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Liters *
                   </label>
                   <input
                     type="number"
                     step="0.01"
                     min="0"
                     value={item.liters}
                     onChange={(e) => handleFuelItemChange(index, 'liters', e.target.value)}
                     className={`input ${errors[`fuelItem${index}Liters`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                     placeholder="0.00"
                   />
                   {errors[`fuelItem${index}Liters`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}Liters`]}</p>}
                 </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.amount}
                    onChange={(e) => handleFuelItemChange(index, 'amount', e.target.value)}
                    className={`input ${errors[`fuelItem${index}Amount`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="0.00"
                  />
                  {errors[`fuelItem${index}Amount`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}Amount`]}</p>}
                </div>

                {/* Vehicle Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Hours *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.vehicleHours}
                    onChange={(e) => handleFuelItemChange(index, 'vehicleHours', e.target.value)}
                    className={`input ${errors[`fuelItem${index}VehicleHours`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="0.00"
                  />
                  {errors[`fuelItem${index}VehicleHours`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}VehicleHours`]}</p>}
                </div>

                {/* Fuel Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={item.fuelDate}
                    onChange={(e) => handleFuelItemChange(index, 'fuelDate', e.target.value)}
                    className={`input ${errors[`fuelItem${index}FuelDate`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                  {errors[`fuelItem${index}FuelDate`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}FuelDate`]}</p>}
                </div>

                {/* Bill Number in Bunk */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bill No in Bunk *
                  </label>
                  <input
                    type="text"
                    value={item.billNumber}
                    onChange={(e) => handleFuelItemChange(index, 'billNumber', e.target.value)}
                    className={`input ${errors[`fuelItem${index}BillNumber`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter bill number"
                  />
                  {errors[`fuelItem${index}BillNumber`] && <p className="text-red-500 text-xs mt-1">{errors[`fuelItem${index}BillNumber`]}</p>}
                </div>

                
              </div>
            </div>
          ))}
        </div>
      </div>

             {/* Summary */}
       <div className="bg-green-50 p-4 rounded-lg border border-green-200">
         <h3 className="font-medium text-green-900 mb-4">Summary</h3>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <div>
             <label className="block text-sm font-medium text-green-700 mb-1">
               Total Liters
             </label>
             <div className="relative">
               <input
                 type="number"
                 value={totalLiters.toFixed(2)}
                 className="input pr-10 bg-green-50"
                 placeholder="Auto-calculated"
                 readOnly
               />
               <Fuel className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-400" />
             </div>
           </div>

           <div>
             <label className="block text-sm font-medium text-green-700 mb-1">
               Total Amount (₹)
             </label>
             <div className="relative">
               <input
                 type="number"
                 value={totalAmount.toFixed(2)}
                 className="input pr-10 bg-green-50"
                 placeholder="Auto-calculated"
                 readOnly
               />
               <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-400" />
             </div>
           </div>

           

           <div>
             <label htmlFor="amountGivenToBunk" className="block text-sm font-medium text-green-700 mb-1">
               Amount Given to Bunk (₹) *
             </label>
             <div className="relative">
               <input
                 type="number"
                 step="0.01"
                 min="0"
                 id="amountGivenToBunk"
                 name="amountGivenToBunk"
                 value={formData.amountGivenToBunk}
                 onChange={handleChange}
                 className={`input pr-10 ${errors.amountGivenToBunk ? 'border-red-500 focus:ring-red-500' : ''}`}
                 placeholder="0.00"
               />
               <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-400" />
             </div>
             {errors.amountGivenToBunk && <p className="text-red-500 text-xs mt-1">{errors.amountGivenToBunk}</p>}
           </div>
         </div>

                   {/* Payment Method */}
          <div className="mt-4">
            <label htmlFor="paymentMethod" className="block text-sm font-medium text-green-700 mb-1">
              Amount or Cash *
            </label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleChange}
              className={`input ${errors.paymentMethod ? 'border-red-500 focus:ring-red-500' : ''}`}
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
            </select>
            {errors.paymentMethod && <p className="text-red-500 text-xs mt-1">{errors.paymentMethod}</p>}
          </div>

          {/* Description */}
          <div className="mt-4">
            <label htmlFor="description" className="block text-sm font-medium text-green-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              rows={3}
              className="input"
              placeholder="Enter description for this fuel entry..."
            />
          </div>
       </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="input"
          placeholder="Additional notes..."
        />
      </div>

             {/* Form Actions */}
       <div className="flex justify-end space-x-3">
         <button
           type="button"
           onClick={onCancel}
           className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-all duration-200"
         >
           Cancel
         </button>
         <button
           type="submit"
           disabled={isSubmitting}
           className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-orange to-brand-red border border-transparent rounded-lg hover:from-orange-500 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
         >
           {isSubmitting ? 'Saving...' : fuelEntry ? 'Update Fuel Entry' : 'Add Fuel Entry'}
         </button>
       </div>
    </form>
  )
}

export default FuelForm

